<script setup lang="ts">
import { getMonthDayYear } from '~/utils/day-js'

defineOptions({
	name: 'TicketScannerResult',
})

const { t: $t, locale } = useI18n()

const emit = defineEmits(['restart'])

const yourNumbers = [5, 19, 26, 37, 40]
const yourStars = [42, 46]
const winNumbers = [5, 19, 26, 37, 44]
const winStars = [44, 44]

const details = computed(() => [
	{ label: $t('Game'), value: $t('EuroMillions') },
	{ label: $t('Purchase Date'), value: getMonthDayYear('2026-02-17', locale.value) },
	{ label: $t('Scan Method'), value: $t('Camera') },
])
</script>

<template>
	<div class="ticket-card overflow-hidden">
		<!-- Win header -->
		<header class="flex justify-between items-center gap-3 bg-green-600 py-2.5 smm:py-3.5 px-2.5 smm:px-4">
			<div class="flex item-center gap-2">
				<div class="flex justify-center items-center size-5 smm:size-7 rounded-lg bg-neutral-50/20">
					<UIcon
						name="heroicons:trophy"
						class="size-2.5 smm:size-4 text-white"
					/>
				</div>

				<div>
					<p class="text-[8px] smm:text-[10px] leading-3 text-white/80">{{ $t('Status') }}</p>
					<p class="text-[10px] smm:text-sm font-bold text-white leading-5">{{ $t('Winner') }}</p>
				</div>
			</div>

			<div class="text-right">
				<p class="text-[8px] smm:text-[10px] leading-3 text-white/80">{{ $t('Won') }}</p>
				<p class="text-[10px] smm:text-base font-base font-bold text-white leading-6">
					{{ locale === 'fr' ? '200 €' : locale === 'en' ? '$200' : '€200' }}
				</p>
			</div>
		</header>

		<div class="px-1.5 smm:px-4 py-4">
			<!-- Meta fields -->
			<div class="grid grid-cols-2 gap-2">
				<div
					v-for="item in details"
					:key="item.label"
					class="rounded-lg p-2 smm:p-3 bg-[#252433]"
					:class="{ 'col-span-2': item.label === details[2]?.label }"
				>
					<p class="text-[6px] smm:text-[8px] leading-3 text-[#8b8aa0]">
						{{ item.label }}
					</p>
					<p class="text-white text-[8px] smm:text-[10px] font-semibold leading-4 capitalize">
						{{ item.value }}
					</p>
				</div>
			</div>

			<!-- Your numbers -->
			<div class="my-4 space-y-2">
				<div class="flex justify-between items-center">
					<p class="text-[10px] smm:text-xs font-bold text-white leading-4">{{ $t('Your Numbers') }}</p>
					<button
						type="button"
						class="flex justify-center items-center gap-1 text-informative-500 text-[7px] smm:text-[10px]"
					>
						<UIcon name="i-ic:outline-mode-edit" />
						{{ $t('Edit Numbers') }}
					</button>
				</div>
				<div class="flex flex-wrap gap-1.5">
					<span
						v-for="n in yourNumbers"
						:key="'y' + n"
						class="bg-green-500 text-white flex justify-center items-center size-5 smm:size-8 rounded-full text-[8px] smm:text-[10px] font-bold leading-4"
					>
						{{ String(n).padStart(2, '0') }}
					</span>
				</div>
				<div class="flex gap-1.5 mt-1.5">
					<span
						v-for="n in yourStars"
						:key="'ys' + n"
						class="bg-warning-400 text-white flex justify-center items-center size-5 smm:size-8 rounded-full text-[8px] smm:text-[10px] font-bold leading-4"
					>
						{{ String(n).padStart(2, '0') }}
					</span>
				</div>
			</div>

			<!-- Winning numbers -->
			<div class="mb-5">
				<p class="text-[10px] smm:text-xs font-bold text-white leading-4 mb-2">{{ $t('Winning Numbers') }}</p>
				<div class="flex flex-wrap gap-1.5">
					<span
						v-for="n in winNumbers"
						:key="'w' + n"
						class="flex justify-center items-center size-5 smm:size-8 rounded-full text-[8px] smm:text-[10px] font-bold leading-4 border-2 border-[#2E2D3D] text-[#8B8AA0] bg-[#252433]"
					>
						{{ String(n).padStart(2, '0') }}
					</span>
				</div>
				<div class="flex gap-1.5 mt-2">
					<span
						v-for="n in winStars"
						:key="'ws' + n"
						class="flex justify-center items-center size-5 smm:size-8 rounded-full text-[8px] smm:text-[10px] font-bold leading-4 border smm:border-2 border-[#2E2D3D] text-[#8B8AA0] bg-[#252433]"
					>
						{{ String(n).padStart(2, '0') }}
					</span>
				</div>
			</div>

			<!-- Replay -->
			<button
				class="w-full py-3 rounded-full bg-primary text-white font-bold text-[8px] smm:text-xs flex items-center justify-center gap-2 hover:brightness-110 transition-all active:scale-95"
				@click="emit('restart')"
			>
				<UIcon
					name="i-teenyicons:gift-outline"
					class="size-2.5 smm:size-4"
				/>
				{{ $t('How to Claim Prize') }}
			</button>
		</div>
	</div>
</template>
